import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import {
  ShieldCheck,
  Plus,
  MagnifyingGlass,
  Trash,
  Buildings,
  Clock,
  Spinner,
  Warning,
  X,
  CheckCircle,
  Star,
  EnvelopeSimple,
  User,
  Phone,
  IdentificationCard,
  ProhibitInset,
  CaretDown,
  CurrencyDollar,
} from '@phosphor-icons/react'
import {
  getPlatformAgents,
  getPlatformAgentCandidates,
  setPlatformAgent,
  removePlatformAgent,
  createPlatformAgent,
  resendPlatformAgentInvite,
  getExclusions,
  addExclusion,
  removeExclusion,
  getAvailableShippersForExclusion,
  getPendingPlatformInvites,
  resendPlatformInvite,
  cancelPlatformInvite,
  getPlatformAgentFees,
  updatePlatformAgentFees,
  PlatformAgent,
  PlatformAgentCandidate,
  ShipperExclusion,
  AvailableShipperForExclusion,
  PendingPlatformInvite,
  PlatformAgentFees,
} from '../../services/platform-agents'

type ModalTabType = 'existing' | 'new'
type PageTabType = 'agents' | 'pending'

export default function PlatformAgentsPage() {
  const navigate = useNavigate()
  const { showSuccess, showError, showWarning } = useDialog()
  const [isLoading, setIsLoading] = useState(true)
  const [agents, setAgents] = useState<PlatformAgent[]>([])
  const [pendingInvites, setPendingInvites] = useState<PendingPlatformInvite[]>([])
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [removeConfirm, setRemoveConfirm] = useState<string | null>(null)
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [pageTab, setPageTab] = useState<PageTabType>('agents')

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTab, setModalTab] = useState<ModalTabType>('existing')
  const [candidates, setCandidates] = useState<PlatformAgentCandidate[]>([])
  const [candidateSearch, setCandidateSearch] = useState('')
  const [isCandidatesLoading, setIsCandidatesLoading] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null)

  // New agent form state
  const [newAgentForm, setNewAgentForm] = useState({
    email: '',
    companyName: '',
    document: '',
    contactName: '',
    phone: '',
  })

  // Exclusions modal state
  const [isExclusionsModalOpen, setIsExclusionsModalOpen] = useState(false)
  const [selectedAgentForExclusions, setSelectedAgentForExclusions] = useState<PlatformAgent | null>(null)
  const [exclusions, setExclusions] = useState<ShipperExclusion[]>([])
  const [availableShippers, setAvailableShippers] = useState<AvailableShipperForExclusion[]>([])
  const [isExclusionsLoading, setIsExclusionsLoading] = useState(false)
  const [selectedShipperToExclude, setSelectedShipperToExclude] = useState<string>('')
  const [exclusionReason, setExclusionReason] = useState('')
  const [exclusionActionLoading, setExclusionActionLoading] = useState<string | null>(null)

  // Fees modal state
  const [isFeesModalOpen, setIsFeesModalOpen] = useState(false)
  const [selectedAgentForFees, setSelectedAgentForFees] = useState<PlatformAgent | null>(null)
  const [fees, setFees] = useState<PlatformAgentFees>({ feeDesconsolidacao: 60, feeLogistic: 80 })
  const [isFeesLoading, setIsFeesLoading] = useState(false)
  const [feesActionLoading, setFeesActionLoading] = useState(false)

  // Load platform agents and pending invites
  const loadAgents = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [agentsData, invitesData] = await Promise.all([
        getPlatformAgents(),
        getPendingPlatformInvites(),
      ])
      setAgents(agentsData)
      setPendingInvites(invitesData)
    } catch (err: any) {
      console.error('Error loading platform agents:', err)
      setError(err.response?.data?.error || 'Erro ao carregar agentes')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadAgents()
  }, [])

  // Load candidates when modal opens
  const loadCandidates = async (search?: string) => {
    setIsCandidatesLoading(true)
    try {
      const data = await getPlatformAgentCandidates({ search })
      setCandidates(data)
    } catch (err: any) {
      console.error('Error loading candidates:', err)
    } finally {
      setIsCandidatesLoading(false)
    }
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
    setModalTab('existing')
    setSelectedCandidate(null)
    setCandidateSearch('')
    setNewAgentForm({
      email: '',
      companyName: '',
      document: '',
      contactName: '',
      phone: '',
    })
    loadCandidates()
  }

  // Filter agents by search
  const filteredAgents = agents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.tradingName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.document?.includes(searchTerm) ||
      agent.tenantName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Filter pending invites by search
  const filteredPendingInvites = pendingInvites.filter(
    (invite) =>
      invite.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invite.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invite.contactName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Handle add platform agent
  const handleAddPlatformAgent = async () => {
    if (!selectedCandidate) return

    setActionLoading(selectedCandidate)
    try {
      await setPlatformAgent(selectedCandidate)
      setIsModalOpen(false)
      loadAgents()
    } catch (err: any) {
      console.error('Error adding platform agent:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao adicionar agente')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle create new agent (creates invite)
  const handleCreateNewAgent = async () => {
    if (!newAgentForm.email || !newAgentForm.companyName) {
      showWarning('Campos obrigatórios', 'Preencha o email e o nome da empresa')
      return
    }

    setActionLoading('creating')
    try {
      await createPlatformAgent({
        email: newAgentForm.email,
        companyName: newAgentForm.companyName,
        document: newAgentForm.document || undefined,
        contactName: newAgentForm.contactName || undefined,
        phone: newAgentForm.phone || undefined,
      })
      setIsModalOpen(false)
      setPageTab('pending') // Switch to pending invites tab
      loadAgents()
    } catch (err: any) {
      console.error('Error creating platform agent:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao criar convite')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle remove platform agent
  const handleRemovePlatformAgent = async (agentId: string) => {
    setActionLoading(agentId)
    try {
      await removePlatformAgent(agentId)
      setAgents((prev) => prev.filter((a) => a.id !== agentId))
      setRemoveConfirm(null)
    } catch (err: any) {
      console.error('Error removing platform agent:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao remover agente')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle resend invite for existing agent (without users)
  const handleResendAgentInvite = async (agentId: string) => {
    setActionLoading(`resend-${agentId}`)
    try {
      const result = await resendPlatformAgentInvite(agentId)
      showSuccess('Convite Reenviado', `Convite reenviado para ${result.email}`)
    } catch (err: any) {
      console.error('Error resending invite:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao reenviar convite')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle resend pending invite
  const handleResendPendingInvite = async (inviteId: string) => {
    setActionLoading(`resend-${inviteId}`)
    try {
      const result = await resendPlatformInvite(inviteId)
      showSuccess('Convite Reenviado', `Convite reenviado para ${result.email}`)
    } catch (err: any) {
      console.error('Error resending invite:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao reenviar convite')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle cancel pending invite
  const handleCancelPendingInvite = async (inviteId: string) => {
    setActionLoading(inviteId)
    try {
      await cancelPlatformInvite(inviteId)
      setPendingInvites((prev) => prev.filter((i) => i.id !== inviteId))
      setCancelConfirm(null)
    } catch (err: any) {
      console.error('Error cancelling invite:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao cancelar convite')
    } finally {
      setActionLoading(null)
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Load exclusions for a platform agent
  const loadExclusions = async (agentId: string) => {
    setIsExclusionsLoading(true)
    try {
      const [exclusionsData, shippersData] = await Promise.all([
        getExclusions(agentId),
        getAvailableShippersForExclusion(agentId),
      ])
      setExclusions(exclusionsData)
      setAvailableShippers(shippersData)
    } catch (err: any) {
      console.error('Error loading exclusions:', err)
    } finally {
      setIsExclusionsLoading(false)
    }
  }

  // Open exclusions modal
  const handleOpenExclusionsModal = (agent: PlatformAgent) => {
    setSelectedAgentForExclusions(agent)
    setIsExclusionsModalOpen(true)
    setSelectedShipperToExclude('')
    setExclusionReason('')
    loadExclusions(agent.id)
  }

  // Add exclusion
  const handleAddExclusion = async () => {
    if (!selectedAgentForExclusions || !selectedShipperToExclude) return

    setExclusionActionLoading('adding')
    try {
      await addExclusion(selectedAgentForExclusions.id, selectedShipperToExclude, exclusionReason || undefined)
      setSelectedShipperToExclude('')
      setExclusionReason('')
      loadExclusions(selectedAgentForExclusions.id)
    } catch (err: any) {
      console.error('Error adding exclusion:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao adicionar exclusão')
    } finally {
      setExclusionActionLoading(null)
    }
  }

  // Remove exclusion
  const handleRemoveExclusion = async (shipperId: string) => {
    if (!selectedAgentForExclusions) return

    setExclusionActionLoading(shipperId)
    try {
      await removeExclusion(selectedAgentForExclusions.id, shipperId)
      loadExclusions(selectedAgentForExclusions.id)
    } catch (err: any) {
      console.error('Error removing exclusion:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao remover exclusão')
    } finally {
      setExclusionActionLoading(null)
    }
  }

  // Load fees for a platform agent
  const loadFees = async (agentId: string) => {
    setIsFeesLoading(true)
    try {
      const data = await getPlatformAgentFees(agentId)
      setFees(data.fees)
    } catch (err: any) {
      console.error('Error loading fees:', err)
    } finally {
      setIsFeesLoading(false)
    }
  }

  // Open fees modal
  const handleOpenFeesModal = (agent: PlatformAgent) => {
    setSelectedAgentForFees(agent)
    setIsFeesModalOpen(true)
    loadFees(agent.id)
  }

  // Save fees
  const handleSaveFees = async () => {
    if (!selectedAgentForFees) return

    setFeesActionLoading(true)
    try {
      await updatePlatformAgentFees(selectedAgentForFees.id, fees)
      setIsFeesModalOpen(false)
      showSuccess('Sucesso', 'Taxas atualizadas com sucesso')
    } catch (err: any) {
      console.error('Error saving fees:', err)
      showError('Erro', err.response?.data?.error || 'Erro ao salvar taxas')
    } finally {
      setFeesActionLoading(false)
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <ShieldCheck size={28} className="text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Agentes da Plataforma</h1>
                <p className="text-sm text-gray-500">
                  Gerencie os agentes homologados da plataforma
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/master/invite-agent')}
                className="gap-2"
              >
                <Plus size={20} weight="bold" />
                Adicionar Usuário
              </Button>
              <Button
                onClick={handleOpenModal}
                className="gap-2"
                style={{ backgroundColor: 'rgb(5, 150, 105)' }}
              >
                <Plus size={20} weight="bold" />
                Adicionar Agente
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Page Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setPageTab('agents')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                pageTab === 'agents'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Agentes da Plataforma ({agents.length})
            </button>
            <button
              onClick={() => setPageTab('pending')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                pageTab === 'pending'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <EnvelopeSimple size={18} />
              Convites Pendentes ({pendingInvites.length})
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Buscar agente por nome, documento ou tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <Warning size={24} className="text-red-500" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadAgents}
              className="ml-auto text-red-600 hover:text-red-800 underline text-sm"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={40} className="animate-spin text-emerald-600" />
          </div>
        ) : pageTab === 'agents' ? (
          /* Platform Agents Tab */
          filteredAgents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ShieldCheck size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">
                  {searchTerm
                    ? 'Nenhum agente encontrado'
                    : 'Nenhum agente da plataforma'}
                </p>
                {!searchTerm && (
                  <p className="text-sm text-gray-400 mt-1">
                    Adicione agentes homologados para disponibiliza-los aos embarcadores
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredAgents.map((agent) => (
                <Card key={agent.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                          <Star size={24} weight="fill" className="text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{agent.name}</h3>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              <ShieldCheck size={12} weight="fill" />
                              Plataforma
                            </span>
                          </div>
                          {agent.tradingName && (
                            <p className="text-sm text-gray-500">{agent.tradingName}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>CNPJ: {agent.document}</span>
                            <span>Tenant: {agent.tenantName}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              Desde: {formatDate(agent.platformAgentSince)}
                            </span>
                            {agent.approvedByName && (
                              <span>Aprovado por: {agent.approvedByName}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {removeConfirm === agent.id ? (
                          <div className="flex items-center gap-2 bg-red-50 p-2 rounded-lg">
                            <span className="text-sm text-red-700">Remover?</span>
                            <button
                              onClick={() => handleRemovePlatformAgent(agent.id)}
                              disabled={actionLoading === agent.id}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              {actionLoading === agent.id ? (
                                <Spinner size={16} className="animate-spin" />
                              ) : (
                                'Sim'
                              )}
                            </button>
                            <button
                              onClick={() => setRemoveConfirm(null)}
                              className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                            >
                              Nao
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleOpenFeesModal(agent)}
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Configurar Taxas"
                            >
                              <CurrencyDollar size={20} />
                            </button>
                            <button
                              onClick={() => handleOpenExclusionsModal(agent)}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Gerenciar Excecoes"
                            >
                              <ProhibitInset size={20} />
                            </button>
                            <button
                              onClick={() => setRemoveConfirm(agent.id)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remover status de plataforma"
                            >
                              <Trash size={20} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          /* Pending Invites Tab */
          filteredPendingInvites.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <EnvelopeSimple size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">
                  {searchTerm
                    ? 'Nenhum convite encontrado'
                    : 'Nenhum convite pendente'}
                </p>
                {!searchTerm && (
                  <p className="text-sm text-gray-400 mt-1">
                    Todos os convites foram aceitos ou cancelados
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredPendingInvites.map((invite) => (
                <Card key={invite.id} className="hover:shadow-md transition-shadow border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                          <EnvelopeSimple size={24} className="text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{invite.companyName}</h3>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <Clock size={12} />
                              Aguardando Aceite
                            </span>
                          </div>
                          {invite.contactName && (
                            <p className="text-sm text-gray-500">Contato: {invite.contactName}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <EnvelopeSimple size={14} />
                              {invite.email}
                            </span>
                            {invite.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={14} />
                                {invite.phone}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              Enviado em: {formatDate(invite.createdAt)}
                            </span>
                            <span>
                              Expira em: {formatDate(invite.expiresAt)}
                            </span>
                            {invite.invitedByName && (
                              <span>Por: {invite.invitedByName}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleResendPendingInvite(invite.id)}
                          disabled={actionLoading === `resend-${invite.id}`}
                          variant="outline"
                          className="gap-2 text-amber-600 border-amber-300 hover:bg-amber-50"
                        >
                          {actionLoading === `resend-${invite.id}` ? (
                            <Spinner size={16} className="animate-spin" />
                          ) : (
                            <EnvelopeSimple size={16} />
                          )}
                          Reenviar
                        </Button>
                        {cancelConfirm === invite.id ? (
                          <div className="flex items-center gap-2 bg-red-50 p-2 rounded-lg">
                            <span className="text-sm text-red-700">Cancelar?</span>
                            <button
                              onClick={() => handleCancelPendingInvite(invite.id)}
                              disabled={actionLoading === invite.id}
                              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              {actionLoading === invite.id ? (
                                <Spinner size={16} className="animate-spin" />
                              ) : (
                                'Sim'
                              )}
                            </button>
                            <button
                              onClick={() => setCancelConfirm(null)}
                              className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                            >
                              Nao
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setCancelConfirm(invite.id)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancelar convite"
                          >
                            <Trash size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}
      </main>

      {/* Exclusions Modal */}
      {isExclusionsModalOpen && selectedAgentForExclusions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsExclusionsModalOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Gerenciar Excecoes</h2>
                <p className="text-sm text-gray-500">{selectedAgentForExclusions.name}</p>
              </div>
              <button
                onClick={() => setIsExclusionsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Add new exclusion */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Adicionar Embarcador a Excecao</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Embarcadores na lista de excecao nao verao este agente automaticamente como "Agente da Plataforma".
                </p>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <div className="relative">
                      <select
                        value={selectedShipperToExclude}
                        onChange={(e) => setSelectedShipperToExclude(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg appearance-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                      >
                        <option value="">Selecione um embarcador...</option>
                        {availableShippers.map((shipper) => (
                          <option key={shipper.id} value={shipper.id}>
                            {shipper.name} {shipper.document ? `(${shipper.document})` : ''}
                          </option>
                        ))}
                      </select>
                      <CaretDown
                        size={16}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleAddExclusion}
                    disabled={!selectedShipperToExclude || exclusionActionLoading === 'adding'}
                    className="gap-2"
                    style={{ backgroundColor: 'rgb(217, 119, 6)' }}
                  >
                    {exclusionActionLoading === 'adding' ? (
                      <Spinner size={20} className="animate-spin" />
                    ) : (
                      <Plus size={20} />
                    )}
                    Adicionar
                  </Button>
                </div>

                <div className="mt-3">
                  <input
                    type="text"
                    placeholder="Motivo da excecao (opcional)"
                    value={exclusionReason}
                    onChange={(e) => setExclusionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Exclusions list */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Embarcadores em Excecao ({exclusions.length})
                </h3>

                {isExclusionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size={32} className="animate-spin text-amber-600" />
                  </div>
                ) : exclusions.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <ProhibitInset size={40} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Nenhum embarcador em excecao</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Todos os embarcadores veem este agente como "Plataforma"
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {exclusions.map((exclusion) => (
                      <div
                        key={exclusion.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Buildings size={20} className="text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{exclusion.shipperName}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {exclusion.shipperDocument && <span>{exclusion.shipperDocument}</span>}
                              <span>-</span>
                              <span>Excluido em {formatDate(exclusion.excludedAt)}</span>
                            </div>
                            {exclusion.reason && (
                              <p className="text-xs text-gray-400 mt-1">Motivo: {exclusion.reason}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveExclusion(exclusion.shipperId)}
                          disabled={exclusionActionLoading === exclusion.shipperId}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Remover da lista de excecao"
                        >
                          {exclusionActionLoading === exclusion.shipperId ? (
                            <Spinner size={18} className="animate-spin" />
                          ) : (
                            <Trash size={18} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Button variant="outline" onClick={() => setIsExclusionsModalOpen(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Platform Agent Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsModalOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Adicionar Agente da Plataforma</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setModalTab('existing')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  modalTab === 'existing'
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Buildings size={20} />
                Agentes Existentes
              </button>
              <button
                onClick={() => setModalTab('new')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  modalTab === 'new'
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <EnvelopeSimple size={20} />
                Cadastrar Novo
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Existing Agents Tab */}
              {modalTab === 'existing' && (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <MagnifyingGlass
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Buscar agente de carga..."
                        value={candidateSearch}
                        onChange={(e) => {
                          setCandidateSearch(e.target.value)
                          loadCandidates(e.target.value)
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Candidates List */}
                  {isCandidatesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner size={32} className="animate-spin text-emerald-600" />
                    </div>
                  ) : candidates.length === 0 ? (
                    <div className="text-center py-8">
                      <Buildings size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">
                        {candidateSearch
                          ? 'Nenhum agente encontrado'
                          : 'Nenhum agente disponivel'}
                      </p>
                      <p className="text-sm text-gray-400 mt-2">
                        Use a aba "Cadastrar Novo" para adicionar um agente
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {candidates.map((candidate) => (
                        <div
                          key={candidate.id}
                          onClick={() => setSelectedCandidate(candidate.id)}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedCandidate === candidate.id
                              ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  selectedCandidate === candidate.id
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                <Buildings size={20} />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">{candidate.name}</h4>
                                {candidate.tradingName && (
                                  <p className="text-sm text-gray-500">{candidate.tradingName}</p>
                                )}
                                <p className="text-xs text-gray-400">
                                  {candidate.document} - {candidate.tenantName}
                                </p>
                              </div>
                            </div>
                            {selectedCandidate === candidate.id && (
                              <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* New Agent Tab */}
              {modalTab === 'new' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 mb-4">
                    Cadastre um novo agente de carga na plataforma. Um convite sera enviado por email.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <EnvelopeSimple
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="email"
                        placeholder="email@agente.com"
                        value={newAgentForm.email}
                        onChange={(e) =>
                          setNewAgentForm({ ...newAgentForm, email: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Buildings
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Nome da empresa"
                        value={newAgentForm.companyName}
                        onChange={(e) =>
                          setNewAgentForm({ ...newAgentForm, companyName: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ
                    </label>
                    <div className="relative">
                      <IdentificationCard
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="00.000.000/0001-00"
                        value={newAgentForm.document}
                        onChange={(e) =>
                          setNewAgentForm({ ...newAgentForm, document: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Contato
                    </label>
                    <div className="relative">
                      <User
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Nome do responsavel"
                        value={newAgentForm.contactName}
                        onChange={(e) =>
                          setNewAgentForm({ ...newAgentForm, contactName: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <div className="relative">
                      <Phone
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="tel"
                        placeholder="+55 11 99999-9999"
                        value={newAgentForm.phone}
                        onChange={(e) =>
                          setNewAgentForm({ ...newAgentForm, phone: e.target.value })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={modalTab === 'existing' ? handleAddPlatformAgent : handleCreateNewAgent}
                disabled={
                  actionLoading !== null ||
                  (modalTab === 'existing' && !selectedCandidate) ||
                  (modalTab === 'new' && (!newAgentForm.email || !newAgentForm.companyName))
                }
                className="gap-2"
                style={{ backgroundColor: 'rgb(5, 150, 105)' }}
              >
                {actionLoading ? (
                  <>
                    <Spinner size={20} className="animate-spin" />
                    {modalTab === 'existing' ? 'Adicionando...' : 'Cadastrando...'}
                  </>
                ) : (
                  <>
                    <ShieldCheck size={20} />
                    {modalTab === 'existing'
                      ? 'Adicionar como Agente da Plataforma'
                      : 'Cadastrar Agente da Plataforma'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fees Configuration Modal */}
      {isFeesModalOpen && selectedAgentForFees && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsFeesModalOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Configurar Taxas</h2>
                <p className="text-sm text-gray-500">{selectedAgentForFees.name}</p>
              </div>
              <button
                onClick={() => setIsFeesModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {isFeesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size={32} className="animate-spin text-emerald-600" />
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-500 mb-4">
                    Configure as taxas automaticas que serao aplicadas quando este agente criar ofertas.
                    Os valores sao em USD.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Taxa de Desconsolidacao (USD)
                    </label>
                    <div className="relative">
                      <CurrencyDollar
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="60.00"
                        value={fees.feeDesconsolidacao}
                        onChange={(e) =>
                          setFees({ ...fees, feeDesconsolidacao: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Aplicada na categoria "Destino"
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logistic Fee (USD)
                    </label>
                    <div className="relative">
                      <CurrencyDollar
                        size={20}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="80.00"
                        value={fees.feeLogistic}
                        onChange={(e) =>
                          setFees({ ...fees, feeLogistic: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Aplicada na categoria "Adicional"
                    </p>
                  </div>

                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <strong>Importante:</strong> Estas taxas serao injetadas automaticamente em
                      todas as novas ofertas deste agente. O agente nao podera remover ou alterar
                      estas taxas.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Button variant="outline" onClick={() => setIsFeesModalOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveFees}
                disabled={feesActionLoading || isFeesLoading}
                className="gap-2"
                style={{ backgroundColor: 'rgb(5, 150, 105)' }}
              >
                {feesActionLoading ? (
                  <>
                    <Spinner size={20} className="animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CurrencyDollar size={20} />
                    Salvar Taxas
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
